import {createContext, FC} from "react";
import {PlanSelectFormData} from "../types/planSelectForm";
import {getBackSearchURL} from "../utils/search";
import dayjs from "dayjs";
import {ALLOWED_FORMS_FOR_SESSION_STORAGE} from "../constants/forms";

export interface FormSessionType {
  checkSession: (data: PlanSelectFormData) => boolean
  saveFormData: (formName: string, data: any) => void
  loadFormData: (formName: string) => any | null
  clearAllFormData: () => void
}

const FormSessionContext = createContext<FormSessionType>({} as FormSessionType);


const FormSessionProvider: FC = ({children}) => {
  const timeoutMinutes = 30;
  const alertMessage = "しばらく操作がなかったためセッションタイムアウトが発生しました。\n お手数ですが、トップページから再度お手続きをしていただきますようお願いいたします。"

  const SESSION_KEY_PREFIX = 'ots_form_';

  const checkSession = (planData: PlanSelectFormData) => {
    // 30分で終了
    const startDate = dayjs(planData.timestamp);
    const toDate = dayjs()
    const sessionTime = toDate.diff(startDate, "minute");
    if ( sessionTime >= timeoutMinutes && planData.id !== undefined ) {
      alert(alertMessage);
      window.location.href = getBackSearchURL(planData.searchParams, planData.id, planData.lang);
      return false;
    } else if (planData.id === undefined || planData.timestamp === undefined) {
      window.location.href = window.location.origin + '/';
      return false;
    }
    return true;
  }

  /**
   * フォームデータをsessionStorageに保存
   * @param formName - 保存するフォーム名（form1, form2, form3, form5のみ）
   * @param data - 保存するデータ
   */
  const saveFormData = (formName: string, data: any) => {
    if (!(ALLOWED_FORMS_FOR_SESSION_STORAGE as readonly string[]).includes(formName)) {
      console.warn(`[FormSession] Security: ${formName} is not allowed for sessionStorage`);
      return;
    }

    try {
      const key = SESSION_KEY_PREFIX + formName;
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('[FormSession] sessionStorage save error:', error);
    }
  };

  /**
   * フォームデータをsessionStorageから読み込み
   * @param formName - 読み込むフォーム名
   * @returns 保存されていたデータ、なければnull
   */
  const loadFormData = (formName: string): any | null => {
    if (!(ALLOWED_FORMS_FOR_SESSION_STORAGE as readonly string[]).includes(formName)) {
      return null;
    }

    try {
      const key = SESSION_KEY_PREFIX + formName;
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('[FormSession] sessionStorage load error:', error);
      return null;
    }
  };

  /**
   * すべてのフォームデータをsessionStorageから削除
   * 予約完了時に呼び出される
   */
  const clearAllFormData = () => {
    try {
      ALLOWED_FORMS_FOR_SESSION_STORAGE.forEach(formName => {
        const key = SESSION_KEY_PREFIX + formName;
        sessionStorage.removeItem(key);
      });
    } catch (error) {
      console.error('[FormSession] sessionStorage clear error:', error);
    }
  };

  return (
    <>
      <FormSessionContext.Provider
        value={{ checkSession, saveFormData, loadFormData, clearAllFormData }}
      >
        {children}
      </FormSessionContext.Provider>
    </>
  );
}

export {FormSessionContext, FormSessionProvider}